/* «Текст из аудио и видео» (§7.1): рамка звука, которая принимает и видео (.bcu, BotCore.upload вида audio), запись с микрофона
   в той же рамке и поле «Или ссылка на YouTube» под рамкой — только когда политика публикует youtube и форма — поле ссылки. */
import { useEffect, useId, useRef, useState } from 'react';
import { uiFormat, uiText } from '../../../i18n';
import type { CircleFormState } from '../../studio/circle/CircleFieldInput';
import { toolField, type ToolField } from '../../studio/circle/tool-form';
import { ToolIcon } from '../../studio/circle/ToolIcon';
import { StudioIcon } from '../../studio/StudioIcon';
import { useRecorder } from '../../clone/use-recorder';
import { recordingWav } from '../../tts/recording-wav';
import { shake } from '../../../ui/motion';
import { fileSize, limitsNote, mmss, youtubeId, type TextKeys } from './text-model';
import { flatPeaks, readSourceMeta, type SourceMeta } from './text-media';
import { FrameActs, FrameEmpty, RightsCap, TextWave } from './TextFrameParts';
import { useConsentUpload } from './use-consent-upload';

export function TextSttSource({
  state,
  keys,
  meta,
  setMeta,
  link,
  setLink,
  onRecording,
}: {
  state: CircleFormState;
  keys: TextKeys;
  meta: Record<string, SourceMeta>;
  setMeta(key: string, value: SourceMeta | null): void;
  /** Черновик ссылки (неверная ссылка в заказ не уходит) */
  link: string;
  setLink(value: string): void;
  onRecording(on: boolean): void;
}) {
  const inputId = useId();
  const frame = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const consent = useConsentUpload(state);
  const audio = keys.audio ? toolField(state.form, keys.audio) : undefined;
  const video = keys.video ? toolField(state.form, keys.video) : undefined;
  const sources = [audio, video].filter((field): field is ToolField => !!field);
  const active = sources.find(field => state.inputs[field.key] || state.uploadIds[field.key]);
  const recordMax = audio?.upload?.max_duration_ms ?? 0;
  const disabled = state.locked;
  const fail = (text: string) => {
    setError(text);
    shake(frame.current);
  };
  async function select(file: File, recorded = false) {
    setError('');
    const field = file.type.startsWith('video/') ? video : file.type.startsWith('audio/') || recorded ? audio : undefined;
    if (!field?.upload) return fail(video ? 'Нужна запись или видео' : 'Нужна аудиозапись');
    if (!field.upload.accept.includes(file.type)) return fail(uiFormat('Этот формат не подходит: {0}', limitsNote([field])));
    if (field.upload.max_bytes && file.size > field.upload.max_bytes)
      return fail(uiFormat('Файл больше {0} МБ', Math.round(field.upload.max_bytes / 1048576)));
    const info = await readSourceMeta(file, field.upload.kind === 'video' ? 'video' : 'audio');
    if (field.upload.max_duration_ms && info.durationMs && info.durationMs > field.upload.max_duration_ms)
      return fail(uiFormat('Длиннее {0}, загрузите короче', mmss(field.upload.max_duration_ms)));
    consent.take(file, async (chosen, current) => {
      // Один источник: файл снимает другой файл и ссылку
      for (const other of sources) if (other.key !== field.key && (current.inputs[other.key] || current.uploadIds[other.key])) current.clearUpload(other.key);
      if (keys.url) {
        setLink('');
        current.setInputs(value => {
          const next = { ...value };
          delete next[keys.url!];
          return next;
        });
      }
      setMeta(field.key, info);
      if (!(await current.upload(field.key, chosen))) setMeta(field.key, null);
    });
  }
  const recorder = useRecorder(file => {
    void recordingWav(file, recordMax)
      .then(wav => select(wav, true))
      .catch(() => fail('Не удалось прочитать запись. Попробуйте ещё раз или загрузите файл.'));
  }, recordMax);
  const recording = recorder.state !== 'idle';
  useEffect(() => onRecording(recording), [recording, onRecording]);
  const remove = () => {
    if (!active) return;
    state.clearUpload(active.key);
    setMeta(active.key, null);
    setError('');
  };
  const info = active ? meta[active.key] : undefined;
  const pending = active && state.uploadIds[active.key] && !state.inputs[active.key];
  const note = error || recorder.error || limitsNote(sources);
  const ytOn = !!keys.url && !active && !recording;
  const ytBad = !!link.trim() && !youtubeId(link);
  const linkLabel = ytBad ? 'Это не ссылка на ролик YouTube' : link.trim() ? 'Ссылка на YouTube' : 'Или ссылка на YouTube';
  const writeLink = (value: string) => {
    setLink(value);
    if (!keys.url) return;
    const valid = youtubeId(value) ? value.trim() : '';
    state.setInputs(current => {
      const next = { ...current };
      if (valid) next[keys.url!] = valid;
      else delete next[keys.url!];
      return next;
    });
  };
  const capture = audio && recordMax > 0 && (
    <button
      className="bcu-pl"
      type="button"
      disabled={disabled}
      onClick={() => {
        setError('');
        void recorder.start();
      }}
    >
      <StudioIcon kind="mic" />
      <span>{uiText('Записать')}</span>
    </button>
  );
  return (
    <>
      <div className="pf-up tt-up">
        <div ref={frame} className={`bcu${active || recording ? ' has' : ''}`} id="ttUp">
          <input
            id={inputId}
            type="file"
            hidden
            accept={sources.flatMap(field => field.upload?.accept ?? []).join(',')}
            disabled={disabled || recording}
            aria-label={uiText('Запись или видео')}
            onChange={event => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void select(file);
            }}
          />
          <div className="bcu-st">
            {recording ? (
              <div className="bcu-live ap-src2">
                <div className={`ocard rec${recorder.state === 'recording' ? ' on' : ''}`}>
                  <button
                    className="rb"
                    type="button"
                    aria-label={uiText(recorder.state === 'recording' ? 'Остановить запись' : 'Запрашиваем микрофон')}
                    onClick={() => {
                      recorder.stop();
                    }}
                  >
                    <i />
                  </button>
                  <span className="wavebar live dwv" aria-hidden="true">
                    {flatPeaks.slice(0, 32).map((peak, index) => (
                      <i key={index} style={{ transform: `scaleY(${(peak * 0.6).toFixed(2)})` }} />
                    ))}
                  </span>
                  <span className="rt">
                    <b>{mmss(recorder.elapsed)}</b> · {uiFormat('до {0}', mmss(recordMax))}
                  </span>
                  <button
                    className="x"
                    type="button"
                    aria-label={uiText('Отменить запись')}
                    onClick={() => {
                      recorder.cancel();
                    }}
                  >
                    <StudioIcon kind="close" />
                  </button>
                </div>
              </div>
            ) : !active ? (
              <FrameEmpty
                inputId={inputId}
                icon="wave"
                title={video ? 'Загрузите запись или видео' : 'Загрузите запись'}
                sub="Разговор, интервью, лекция или голосовое"
                note={note}
                bad={!!(error || recorder.error)}
                disabled={disabled}
                capture={capture}
              />
            ) : info?.kind === 'video' ? (
              <div className="bcu-f dk">
                {info.poster && <img className="bcu-bg" src={info.poster} alt="" aria-hidden="true" />}
                <div className="bcu-pa">
                  <span className="bcu-pv" style={{ aspectRatio: String(info.ratio ?? 16 / 9) }}>
                    {info.poster && <img src={info.poster} alt={info.name} />}
                    <i className="bcu-play">
                      <StudioIcon kind="play" />
                    </i>
                    {!!info.durationMs && (
                      <span className="bcu-dur">
                        <StudioIcon kind="play" />
                        <b className="mono">{mmss(info.durationMs)}</b>
                      </span>
                    )}
                  </span>
                </div>
                <FrameActs inputId={inputId} disabled={disabled} onRemove={remove} />
              </div>
            ) : (
              <div className="bcu-f">
                <div className="bcu-au">
                  <span className="bcu-wv" aria-hidden="true">
                    <TextWave peaks={info?.peaks ?? flatPeaks} />
                  </span>
                  <span className="bcu-am">
                    <b>{info?.name ?? uiText(active.upload?.kind === 'video' ? 'Видео' : 'Запись')}</b>
                    <span className="mono">
                      {info
                        ? [info.durationMs ? mmss(info.durationMs) : '', fileSize(info.size)].filter(Boolean).join(' · ')
                        : uiText(state.inputs[active.key] ? 'Загружено' : 'Проверяем файл…')}
                    </span>
                  </span>
                </div>
                <FrameActs inputId={inputId} disabled={disabled} onRemove={remove} />
              </div>
            )}
          </div>
        </div>
        {active && error && (
          <p className="bcu-n bad" role="alert">
            {uiText(error)}
          </p>
        )}
        {pending && (
          <button type="button" className="lnk" disabled={state.busy} onClick={() => void state.upload(active!.key, null)}>
            {uiText('Продолжить проверку')}
          </button>
        )}
      </div>
      {ytOn && (
        <div className="tt-w">
          <label className={`tt-fld${ytBad ? ' bad' : ''}${youtubeId(link) ? ' ok' : ''}`}>
            <span className="ic">
              <ToolIcon kind="link" />
            </span>
            <span className="m">
              <small role={ytBad ? 'alert' : undefined}>{uiText(linkLabel)}</small>
              <input
                type="text"
                inputMode="url"
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                maxLength={300}
                placeholder="youtube.com/watch?v=…"
                aria-label={uiText('Ссылка на ролик YouTube')}
                value={link}
                disabled={disabled}
                onChange={event => writeLink(event.target.value)}
              />
            </span>
            <span className="tt-e">
              {link ? (
                <button className="tt-x" type="button" aria-label={uiText('Очистить')} disabled={disabled} onClick={() => writeLink('')}>
                  <StudioIcon kind="close" />
                </button>
              ) : (
                <button
                  className="tt-paste"
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    void navigator.clipboard
                      ?.readText?.()
                      .then(value => writeLink(value.trim().slice(0, 300)))
                      .catch(() => fail('Не удалось вставить ссылку: вставьте её вручную'));
                  }}
                >
                  {uiText('Вставить')}
                </button>
              )}
            </span>
          </label>
        </div>
      )}
      <RightsCap />
      {consent.sheet}
    </>
  );
}
